import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Users, Ticket, Gift, Check, CreditCard, AlertCircle, Phone, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// Card Element Styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1f2937',
      fontFamily: 'system-ui, sans-serif',
      '::placeholder': {
        color: '#9ca3af',
      },
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

// Shared booking logic hook
const useBookingLogic = (venue, events, vouchers) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  const [date, setDate] = useState(new Date());
  const [partySize, setPartySize] = useState('2');
  const [selectedType, setSelectedType] = useState(venue?.accepts_reservations !== false ? 'table' : '');
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [filteredExperiences, setFilteredExperiences] = useState([]);
  const [privateHirePackages, setPrivateHirePackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAllTimes, setShowAllTimes] = useState(false);
  const [specialRequests, setSpecialRequests] = useState('');
  const [phone, setPhone] = useState('');
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  
  // Card hold states
  const [cardRequired, setCardRequired] = useState(false);
  const [noShowFee, setNoShowFee] = useState(25);
  const [cancellationHours, setCancellationHours] = useState(24);

  useEffect(() => {
    if (venue?.id && date) {
      fetchAvailability();
    }
  }, [venue?.id, date, partySize]);

  const fetchAvailability = async () => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await api.get(`/venues/${venue.id}/availability`, {
        params: { date: dateStr, party_size: parseInt(partySize) }
      });
      
      setAvailableSlots(response.data.available_slots || []);
      setFilteredEvents(response.data.events || []);
      setFilteredExperiences(response.data.experiences || vouchers);
      
      const settings = venue.reservation_settings || {};
      setCardRequired(settings.require_card_hold !== false);
      setNoShowFee(settings.no_show_fee || 25);
      setCancellationHours(settings.cancellation_hours || 24);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
      setAvailableSlots([
        '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM',
        '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM',
        '7:30 PM', '8:00 PM', '8:30 PM', '9:00 PM'
      ]);
      setFilteredEvents(events.filter(e => e.date === format(date, 'yyyy-MM-dd')));
      setFilteredExperiences(vouchers);
    }
    // Fetch private hire packages
    try {
      const pkgRes = await api.get(`/private-hire/packages/${venue.id}`);
      setPrivateHirePackages(pkgRes.data || []);
    } catch { setPrivateHirePackages([]); }
  };

  const getTypeOptions = () => {
    const options = [];
    if (venue?.accepts_reservations !== false) {
      options.push({ value: 'table', label: 'Table Reservation', icon: Users });
    }
    filteredEvents.forEach(event => {
      options.push({ value: `event-${event.id}`, label: event.title, icon: Ticket, price: event.price, isEvent: true });
    });
    filteredExperiences.forEach(exp => {
      options.push({ value: `experience-${exp.id}`, label: exp.title, icon: Gift, price: exp.price, isExperience: true });
    });
    privateHirePackages.forEach(pkg => {
      options.push({ value: `hire-${pkg.id}`, label: `${pkg.name}`, icon: Building2, price: pkg.base_price, isHire: true });
    });
    return options;
  };

  const handleTypeChange = (value) => {
    if (value === 'table') {
      setSelectedType('table');
      setSelectedItemId(null);
    } else if (value.startsWith('event-')) {
      setSelectedType('event');
      setSelectedItemId(value.replace('event-', ''));
    } else if (value.startsWith('experience-')) {
      setSelectedType('experience');
      setSelectedItemId(value.replace('experience-', ''));
    } else if (value.startsWith('hire-')) {
      setSelectedType('hire');
      setSelectedItemId(value.replace('hire-', ''));
    }
    setSelectedTime(null);
  };

  return {
    navigate, isAuthenticated, user, date, setDate, partySize, setPartySize,
    selectedType, selectedItemId, selectedTime, setSelectedTime, availableSlots,
    loading, setLoading, showAllTimes, setShowAllTimes, specialRequests, setSpecialRequests,
    phone, setPhone, bookingSuccess, setBookingSuccess, confirmationMessage, setConfirmationMessage,
    cardRequired, noShowFee, cancellationHours, getTypeOptions, handleTypeChange
  };
};

// Booking Form WITH Stripe (wrapped in Elements)
const BookingFormWithStripe = ({ venue, events = [], vouchers = [] }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  const {
    navigate, isAuthenticated, user, date, setDate, partySize, setPartySize,
    selectedType, selectedItemId, selectedTime, setSelectedTime, availableSlots,
    loading, setLoading, showAllTimes, setShowAllTimes, specialRequests, setSpecialRequests,
    phone, setPhone, bookingSuccess, setBookingSuccess, confirmationMessage, setConfirmationMessage,
    cardRequired, noShowFee, cancellationHours, getTypeOptions, handleTypeChange
  } = useBookingLogic(venue, events, vouchers);

  const handleCardChange = (event) => {
    setCardError(event.error ? event.error.message : null);
    setCardComplete(event.complete);
  };

  const handleCheckAvailability = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to make a reservation');
      navigate('/auth');
      return;
    }

    if (selectedType === 'private-hire') {
      const el = document.querySelector('[data-testid="tab-private-hire"]');
      if (el) el.click();
      return;
    }

    if (selectedType === 'hire' && selectedItemId) {
      navigate(`/private-hire/${selectedItemId}`);
      return;
    }

    if (selectedType === 'event' && selectedItemId) {
      navigate(`/checkout?type=event&id=${selectedItemId}&qty=1`);
      return;
    }
    
    if (selectedType === 'experience' && selectedItemId) {
      navigate(`/checkout?type=voucher&id=${selectedItemId}&qty=1`);
      return;
    }

    if (!selectedTime) {
      toast.error('Please select a time slot');
      return;
    }

    if (!phone || phone.trim().length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    if (cardRequired && !cardComplete) {
      toast.error('Please enter your card details');
      return;
    }

    setLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      let paymentMethodId = null;

      if (cardRequired && stripe && elements) {
        const cardElement = elements.getElement(CardElement);
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: user?.full_name || '',
            email: user?.email || '',
            phone: phone,
          },
        });

        if (error) {
          toast.error(error.message);
          setLoading(false);
          return;
        }
        paymentMethodId = paymentMethod.id;
      }

      const response = await api.post('/reservations', {
        venue_id: venue.id,
        date: dateStr,
        time: selectedTime,
        party_size: parseInt(partySize),
        reservation_type: selectedType,
        item_id: selectedItemId,
        special_requests: specialRequests,
        user_phone: phone,
        payment_method_id: paymentMethodId
      });

      setConfirmationMessage(response.data.confirmation_message || '');
      setBookingSuccess(true);
      toast.success('Reservation confirmed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to make reservation');
    } finally {
      setLoading(false);
    }
  };

  const displayedTimes = showAllTimes ? availableSlots : availableSlots.slice(0, 6);

  if (bookingSuccess) {
    return (
      <Card className="border-2 border-green-500/50 bg-green-50 dark:bg-green-950/20" data-testid="booking-success">
        <CardContent className="p-6 text-center">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-green-700 dark:text-green-400 mb-2">Reservation Confirmed!</h3>
          <p className="text-green-600 dark:text-green-500 mb-2">{format(date, 'EEEE, MMMM d, yyyy')} at {selectedTime}</p>
          <p className="text-sm text-muted-foreground mb-4">{partySize} guests at {venue.name}</p>
          {confirmationMessage && <p className="text-sm text-muted-foreground mb-4 p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">{confirmationMessage}</p>}
          <p className="text-xs text-muted-foreground mb-4">We'll send you a reminder before your reservation.</p>
          <Button onClick={() => setBookingSuccess(false)} variant="outline" className="mr-2">Make Another Reservation</Button>
          <Button onClick={() => navigate('/dashboard')}>View My Reservations</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">How Many & When?</Label>
        <div className="grid grid-cols-2 gap-3">
          <Select value={partySize} onValueChange={setPartySize}>
            <SelectTrigger data-testid="guests-select"><Users className="w-4 h-4 mr-2 text-muted-foreground" /><SelectValue placeholder="Guests" /></SelectTrigger>
            <SelectContent>{Array.from({ length: venue?.max_party_size || 20 }, (_, i) => i + 1).map(num => (<SelectItem key={num} value={String(num)}>{num} {num === 1 ? 'Guest' : 'Guests'}</SelectItem>))}</SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("justify-start text-left font-normal", !date && "text-muted-foreground")} data-testid="date-picker-trigger">
                <CalendarIcon className="w-4 h-4 mr-2" />{date ? format(date, 'MM/dd/yyyy') : 'Pick date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="space-y-3">
        {getTypeOptions().length === 0 && venue?.claimed === false ? (
          <div className="space-y-4">
            {/* Plan a Night Out */}
            <div className="p-4 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <h4 className="font-bold text-sm mb-1">Plan a Night at {venue.name}</h4>
              <p className="text-xs text-muted-foreground mb-3">Create a plan and share it with friends</p>
              <PlanCreator venue={venue} isAuthenticated={isAuthenticated} navigate={navigate} />
            </div>
            <div className="text-center pt-2 border-t">
              <p className="text-[11px] text-muted-foreground mb-1">Own this venue?</p>
              <a href="/partner/register" className="text-xs font-semibold text-amber-600 hover:underline">Claim This Profile</a>
              <span className="text-[10px] text-muted-foreground mx-1">·</span>
              <a href="/partner/register" className="text-[10px] text-muted-foreground hover:underline">Learn More</a>
            </div>
          </div>
        ) : (
        <>
        <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Select Type</Label>
        <Select value={selectedType === 'table' ? 'table' : `${selectedType}-${selectedItemId}`} onValueChange={handleTypeChange}>
          <SelectTrigger data-testid="type-select"><SelectValue placeholder="Choose booking type" /></SelectTrigger>
          <SelectContent>
            {getTypeOptions().map((option) => {
              const Icon = option.icon;
              return (<SelectItem key={option.value} value={option.value}><div className="flex items-center gap-2"><Icon className="w-4 h-4" /><span className="truncate max-w-[180px]">{option.label}</span>{option.price && <Badge variant="secondary" className="ml-auto">${option.price}</Badge>}</div></SelectItem>);
            })}
          </SelectContent>
        </Select>
        </>
        )}
      </div>

      <div className="space-y-3">
        <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">What Time?</Label>
        {availableSlots.length > 0 ? (
          <>
            <div className="grid grid-cols-3 gap-2">
              {displayedTimes.map((time) => (
                <Button key={time} variant={selectedTime === time ? 'default' : 'outline'} size="sm" onClick={() => setSelectedTime(time)} className={cn("text-sm", selectedTime === time && "bg-indigo-600 hover:bg-indigo-700")} data-testid={`time-slot-${time.replace(/[: ]/g, '-')}`}>{time}</Button>
              ))}
            </div>
            {availableSlots.length > 6 && <Button variant="link" size="sm" onClick={() => setShowAllTimes(!showAllTimes)} className="text-indigo-600 p-0 h-auto">{showAllTimes ? 'Show fewer times' : `View all ${availableSlots.length} times`}</Button>}
          </>
        ) : (<p className="text-sm text-muted-foreground">No available times for this date</p>)}
      </div>

      {selectedTime && selectedType === 'table' && (
        <div className="space-y-4 pt-4 border-t">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Phone className="w-3 h-3" />Phone Number *</Label>
            <Input type="tel" placeholder="(555) 123-4567" value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="phone-input" />
            <p className="text-xs text-muted-foreground">Required so the venue can contact you about your reservation</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Special Requests (Optional)</Label>
            <Textarea placeholder="Any special requests or dietary requirements?" value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} rows={2} data-testid="special-requests-input" />
          </div>
          {cardRequired && (
            <div className="space-y-3">
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1"><CreditCard className="w-3 h-3" />Card Details</Label>
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <div className="flex gap-2"><AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" /><div className="text-xs text-amber-800 dark:text-amber-200"><p className="font-medium mb-1">Cancellation Policy</p><p>Your card will be held but not charged. Cancel at least {cancellationHours} hours before your reservation to avoid a ${noShowFee}/person no-show fee.</p></div></div>
              </div>
              <div className="border rounded-lg p-3 bg-white dark:bg-gray-950"><CardElement options={cardElementOptions} onChange={handleCardChange} /></div>
              {cardError && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{cardError}</p>}
            </div>
          )}
        </div>
      )}

      <Button className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" onClick={handleCheckAvailability} disabled={loading || !selectedTime || (selectedType === 'table' && (!phone || (cardRequired && !cardComplete)))} data-testid="check-availability-btn">
        {loading ? <span className="flex items-center gap-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Processing...</span> : selectedType === 'table' ? 'Book Reservation' : 'Check Availability'}
      </Button>
      <p className="text-xs text-center text-muted-foreground">Powered by EVENT★GOERS • Instant Confirmation</p>
    </div>
  );
};

// Booking Form WITHOUT Stripe
const BookingFormNoStripe = ({ venue, events = [], vouchers = [], stripeLoaded }) => {
  const {
    navigate, isAuthenticated, user, date, setDate, partySize, setPartySize,
    selectedType, selectedItemId, selectedTime, setSelectedTime, availableSlots,
    loading, setLoading, showAllTimes, setShowAllTimes, specialRequests, setSpecialRequests,
    phone, setPhone, bookingSuccess, setBookingSuccess, confirmationMessage, setConfirmationMessage,
    cardRequired, noShowFee, cancellationHours, getTypeOptions, handleTypeChange
  } = useBookingLogic(venue, events, vouchers);

  const handleCheckAvailability = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to make a reservation');
      navigate('/auth');
      return;
    }

    if (selectedType === 'private-hire') {
      const el = document.querySelector('[data-testid="tab-private-hire"]');
      if (el) el.click();
      return;
    }

    if (selectedType === 'hire' && selectedItemId) {
      navigate(`/private-hire/${selectedItemId}`);
      return;
    }

    if (selectedType === 'event' && selectedItemId) {
      navigate(`/checkout?type=event&id=${selectedItemId}&qty=1`);
      return;
    }
    
    if (selectedType === 'experience' && selectedItemId) {
      navigate(`/checkout?type=voucher&id=${selectedItemId}&qty=1`);
      return;
    }

    if (!selectedTime) {
      toast.error('Please select a time slot');
      return;
    }

    if (!phone || phone.trim().length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');

      const response = await api.post('/reservations', {
        venue_id: venue.id,
        date: dateStr,
        time: selectedTime,
        party_size: parseInt(partySize),
        reservation_type: selectedType,
        item_id: selectedItemId,
        special_requests: specialRequests,
        user_phone: phone,
        payment_method_id: null
      });

      setConfirmationMessage(response.data.confirmation_message || '');
      setBookingSuccess(true);
      toast.success('Reservation confirmed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to make reservation');
    } finally {
      setLoading(false);
    }
  };

  const displayedTimes = showAllTimes ? availableSlots : availableSlots.slice(0, 6);

  if (bookingSuccess) {
    return (
      <Card className="border-2 border-green-500/50 bg-green-50 dark:bg-green-950/20" data-testid="booking-success">
        <CardContent className="p-6 text-center">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-white" /></div>
          <h3 className="text-xl font-bold text-green-700 dark:text-green-400 mb-2">Reservation Confirmed!</h3>
          <p className="text-green-600 dark:text-green-500 mb-2">{format(date, 'EEEE, MMMM d, yyyy')} at {selectedTime}</p>
          <p className="text-sm text-muted-foreground mb-4">{partySize} guests at {venue.name}</p>
          {confirmationMessage && <p className="text-sm text-muted-foreground mb-4 p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">{confirmationMessage}</p>}
          <Button onClick={() => setBookingSuccess(false)} variant="outline" className="mr-2">Make Another Reservation</Button>
          <Button onClick={() => navigate('/dashboard')}>View My Reservations</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">How Many & When?</Label>
        <div className="grid grid-cols-2 gap-3">
          <Select value={partySize} onValueChange={setPartySize}>
            <SelectTrigger><Users className="w-4 h-4 mr-2 text-muted-foreground" /><SelectValue placeholder="Guests" /></SelectTrigger>
            <SelectContent>{Array.from({ length: venue?.max_party_size || 20 }, (_, i) => i + 1).map(num => (<SelectItem key={num} value={String(num)}>{num} {num === 1 ? 'Guest' : 'Guests'}</SelectItem>))}</SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("justify-start text-left font-normal", !date && "text-muted-foreground")}>
                <CalendarIcon className="w-4 h-4 mr-2" />{date ? format(date, 'MM/dd/yyyy') : 'Pick date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="space-y-3">
        {getTypeOptions().length === 0 && venue?.claimed === false ? (
          <div className="space-y-4">
            {/* Plan a Night Out */}
            <div className="p-4 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <h4 className="font-bold text-sm mb-1">Plan a Night at {venue.name}</h4>
              <p className="text-xs text-muted-foreground mb-3">Create a plan and share it with friends</p>
              <PlanCreator venue={venue} isAuthenticated={isAuthenticated} navigate={navigate} />
            </div>
            <div className="text-center pt-2 border-t">
              <p className="text-[11px] text-muted-foreground mb-1">Own this venue?</p>
              <a href="/partner/register" className="text-xs font-semibold text-amber-600 hover:underline">Claim This Profile</a>
              <span className="text-[10px] text-muted-foreground mx-1">·</span>
              <a href="/partner/register" className="text-[10px] text-muted-foreground hover:underline">Learn More</a>
            </div>
          </div>
        ) : (
        <>
        <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Select Type</Label>
        <Select value={selectedType === 'table' ? 'table' : `${selectedType}-${selectedItemId}`} onValueChange={handleTypeChange}>
          <SelectTrigger><SelectValue placeholder="Choose booking type" /></SelectTrigger>
          <SelectContent>
            {getTypeOptions().map((option) => {
              const Icon = option.icon;
              return (<SelectItem key={option.value} value={option.value}><div className="flex items-center gap-2"><Icon className="w-4 h-4" /><span className="truncate max-w-[180px]">{option.label}</span>{option.price && <Badge variant="secondary" className="ml-auto">${option.price}</Badge>}</div></SelectItem>);
            })}
          </SelectContent>
        </Select>
        </>
        )}
      </div>

      <div className="space-y-3">
        <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">What Time?</Label>
        {availableSlots.length > 0 ? (
          <>
            <div className="grid grid-cols-3 gap-2">
              {displayedTimes.map((time) => (<Button key={time} variant={selectedTime === time ? 'default' : 'outline'} size="sm" onClick={() => setSelectedTime(time)} className={cn("text-sm", selectedTime === time && "bg-indigo-600 hover:bg-indigo-700")}>{time}</Button>))}
            </div>
            {availableSlots.length > 6 && <Button variant="link" size="sm" onClick={() => setShowAllTimes(!showAllTimes)} className="text-indigo-600 p-0 h-auto">{showAllTimes ? 'Show fewer times' : `View all ${availableSlots.length} times`}</Button>}
          </>
        ) : (<p className="text-sm text-muted-foreground">No available times for this date</p>)}
      </div>

      {selectedTime && selectedType === 'table' && (
        <div className="space-y-4 pt-4 border-t">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Phone className="w-3 h-3" />Phone Number *</Label>
            <Input type="tel" placeholder="(555) 123-4567" value={phone} onChange={(e) => setPhone(e.target.value)} required />
            <p className="text-xs text-muted-foreground">Required so the venue can contact you</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Special Requests (Optional)</Label>
            <Textarea placeholder="Any special requests?" value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} rows={2} />
          </div>
          {!stripeLoaded && <p className="text-xs text-muted-foreground">Loading payment options...</p>}
        </div>
      )}

      <Button className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" onClick={handleCheckAvailability} disabled={loading || !selectedTime || (selectedType === 'table' && !phone)}>
        {loading ? <span className="flex items-center gap-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Processing...</span> : selectedType === 'table' ? 'Book Reservation' : 'Check Availability'}
      </Button>
      <p className="text-xs text-center text-muted-foreground">Powered by EVENT★GOERS</p>
    </div>
  );
};


// Plan Creator — inline mini-form for creating plans
const PlanCreator = ({ venue, isAuthenticated, navigate }) => {
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [planName, setPlanName] = useState('');
  const [planDate, setPlanDate] = useState('');
  const [planTime, setPlanTime] = useState('19:00');
  const [planMsg, setPlanMsg] = useState('');

  const handleCreate = async () => {
    if (!planName.trim()) { toast.error('Enter your name'); return; }
    if (!planDate) { toast.error('Pick a date'); return; }
    setCreating(true);
    try {
      const res = await api.post('/plans', { venue_id: venue.id, venue_name: venue.name, creator_name: planName, date: planDate, time: planTime, message: planMsg });
      toast.success('Plan created! Share the link with friends.');
      navigate(`/plans/${res.data.share_code}`);
    } catch { toast.error('Failed'); }
    finally { setCreating(false); }
  };

  if (!showForm) return <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" size="sm" onClick={() => setShowForm(true)}>Create a Plan</Button>;

  return (
    <div className="space-y-2 mt-2">
      <Input placeholder="Your name" value={planName} onChange={e => setPlanName(e.target.value)} className="h-8 text-xs" />
      <Input type="date" value={planDate} onChange={e => setPlanDate(e.target.value)} className="h-8 text-xs" />
      <Input type="time" value={planTime} onChange={e => setPlanTime(e.target.value)} className="h-8 text-xs" />
      <Input placeholder="What's the plan? (optional)" value={planMsg} onChange={e => setPlanMsg(e.target.value)} className="h-8 text-xs" />
      <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" size="sm" onClick={handleCreate} disabled={creating || !planDate || !planName.trim()}>
        {creating ? 'Creating...' : 'Create & Share'}
      </Button>
    </div>
  );
};


// Private Hire Tab — Shows packages with links, fallback to inquiry
const PrivateHireTab = ({ venue }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInquiry, setShowInquiry] = useState(false);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [inquirySubmitted, setInquirySubmitted] = useState(false);
  const [date, setDate] = useState(new Date());
  const [formData, setFormData] = useState({ guests: '20-50', eventType: 'corporate', phone: '', message: '' });

  useEffect(() => {
    Promise.all([
      api.get(`/private-hire/packages/${venue.id}`).catch(() => ({ data: [] })),
      api.get(`/private-hire/spaces/${venue.id}`).catch(() => ({ data: [] })),
    ]).then(([pkgRes, spaceRes]) => { setPackages(pkgRes.data || []); setSpaces(spaceRes.data || []); }).finally(() => setLoading(false));
  }, [venue.id]);

  const handleInquiry = async () => {
    if (!isAuthenticated) { toast.error('Please sign in'); navigate('/auth'); return; }
    if (!formData.message) { toast.error('Please describe your event'); return; }
    setInquiryLoading(true);
    try {
      await api.post('/inquiries/private-hire', { venue_id: venue.id, event_type: formData.eventType, expected_guests: formData.guests, preferred_date: format(date, 'yyyy-MM-dd'), contact_name: user?.full_name, contact_email: user?.email, contact_phone: formData.phone, message: formData.message });
      setInquirySubmitted(true);
      toast.success('Inquiry sent!');
    } catch { toast.error('Failed'); } finally { setInquiryLoading(false); }
  };

  if (loading) return <div className="text-center py-6 text-muted-foreground text-sm">Loading packages...</div>;

  return (
    <div className="space-y-4">
      {/* Packages */}
      {packages.length > 0 ? (
        <>
          <p className="text-sm text-muted-foreground">{packages.length} package{packages.length > 1 ? 's' : ''} available</p>
          {packages.map(pkg => (
            <div key={pkg.id} className="p-3 border rounded-lg hover:border-indigo-500 cursor-pointer transition-colors" onClick={() => navigate(`/private-hire/${pkg.id}`)}>
              <div className="flex items-center gap-2 mb-1">
                <Badge className={pkg.category === 'corporate' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'} variant="secondary">{pkg.category}</Badge>
                <span className="font-bold text-sm">{pkg.name}</span>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-1">{pkg.description}</p>
              <div className="flex justify-between items-center mt-2">
                <span className="text-indigo-600 font-bold text-sm">From ${pkg.base_price?.toLocaleString()}</span>
                <span className="text-[11px] text-muted-foreground">{pkg.duration_label} · {pkg.min_guests}-{pkg.max_guests} guests</span>
              </div>
            </div>
          ))}
          <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setShowInquiry(!showInquiry)}>{showInquiry ? 'Hide Custom Inquiry' : 'Need something custom?'}</Button>
        </>
      ) : (
        <p className="text-sm text-muted-foreground mb-2">Submit an inquiry for private events at {venue.name}.</p>
      )}

      {/* Inquiry Form (shown if no packages or user clicks custom) */}
      {(packages.length === 0 || showInquiry) && !inquirySubmitted && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Select value={formData.guests} onValueChange={v => setFormData({...formData, guests: v})}><SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="10-20">10-20</SelectItem><SelectItem value="20-50">20-50</SelectItem><SelectItem value="50-100">50-100</SelectItem><SelectItem value="100+">100+</SelectItem></SelectContent></Select>
            <Select value={formData.eventType} onValueChange={v => setFormData({...formData, eventType: v})}><SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="corporate">Corporate</SelectItem><SelectItem value="birthday">Birthday</SelectItem><SelectItem value="wedding">Wedding</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select>
          </div>
          <Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start h-9 text-xs"><CalendarIcon className="w-3 h-3 mr-2" />{format(date, 'MMM d, yyyy')}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={date} onSelect={d => d && setDate(d)} disabled={d => d < new Date()} /></PopoverContent></Popover>
          <Input placeholder="Phone number" className="h-9 text-xs" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
          <Textarea placeholder="Describe your event..." className="text-xs" rows={3} value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} />
          <Button className="w-full bg-purple-600 hover:bg-purple-700 h-9" onClick={handleInquiry} disabled={inquiryLoading}>{inquiryLoading ? 'Sending...' : 'Send Inquiry'}</Button>
        </div>
      )}
      {inquirySubmitted && (
        <div className="text-center py-4"><Check className="w-10 h-10 text-green-500 mx-auto mb-2" /><p className="font-bold text-sm">Inquiry Sent!</p><p className="text-xs text-muted-foreground">{venue.name} will respond soon.</p></div>
      )}
    </div>
  );
};

// Main Booking Widget
export const BookingWidget = ({ venue, events = [], vouchers = [] }) => {
  const [activeTab, setActiveTab] = useState('booking');
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeLoaded, setStripeLoaded] = useState(false);

  useEffect(() => {
    const initStripe = async () => {
      try {
        const response = await api.get('/payments/config');
        const key = response.data.publishable_key;
        if (key && key !== 'pk_test_placeholder') {
          setStripePromise(loadStripe(key));
        }
      } catch (error) {
        console.error('Failed to load Stripe config:', error);
      } finally {
        setStripeLoaded(true);
      }
    };
    initStripe();
  }, []);

  return (
    <Card className="border-2 shadow-lg" data-testid="booking-widget">
      <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
        <CardTitle className="text-lg font-bold">BOOK & ENQUIRE</CardTitle>
      </CardHeader>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-2 rounded-none border-b">
          <TabsTrigger value="booking" data-testid="booking-tab">Booking</TabsTrigger>
          <TabsTrigger value="private-hire" data-testid="private-hire-tab">Private Hire</TabsTrigger>
        </TabsList>
        <TabsContent value="booking" className="p-4">
          {stripePromise ? (
            <Elements stripe={stripePromise}><BookingFormWithStripe venue={venue} events={events} vouchers={vouchers} /></Elements>
          ) : (
            <BookingFormNoStripe venue={venue} events={events} vouchers={vouchers} stripeLoaded={stripeLoaded} />
          )}
        </TabsContent>
        <TabsContent value="private-hire" className="p-4 space-y-4"><PrivateHireTab venue={venue} /></TabsContent>
      </Tabs>
    </Card>
  );
};

export default BookingWidget;
